// DLY-01/DLY-02 — courier saga attempt evidence + receipt-gated non-effect declaration, plus
// DLY-02 route actions: own-fleet reassign, outcomes/custody view, return-to-origin receipt and
// FIN-04 return-compensation.
//
// A rare manual-intervention surface, not a primary board feature (the Board's own delivery
// cards deliberately show no per-card dispatch actions yet — "Dispatch actions unavailable until
// current authority is verified"), so this stands alone: look up one delivery by id, see its
// courier booking attempts, attempt a provider read-back, and — only once a read-back has been
// attempted, with a mandatory courier-dashboard reference — declare the current `unknown` attempt
// a non-effect. The backend (app/api/console_portal/dispatch.py) enforces every one of these
// gates independently; this page just can't submit past them without the right inputs. The DLY-02
// actions below were previously API-only (built, never wired to any ops-console page — OPS-01).
import { useCallback, useState } from "react";
import { Ban, RotateCcw, Search, ShieldAlert, Truck, Undo2 } from "lucide-react";

import { ApiError, apiGet, apiPost } from "../api";
import { useAuth } from "../auth";
import { newRequestId } from "../cases";
import type { DeliveryOutcomeView, DispatchAttemptsData } from "../types";

function statusLabel(value: string): string {
  return value.replace(/_/g, " ");
}

export default function DispatchRecovery() {
  const { user } = useAuth();
  const canAct = user?.role === "owner" || user?.role === "admin" || user?.role === "dispatcher";
  const canMoney = user?.role === "owner" || user?.role === "admin";

  const [deliveryId, setDeliveryId] = useState("");
  const [data, setData] = useState<DispatchAttemptsData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [taskRef, setTaskRef] = useState("");
  const [readbackBusy, setReadbackBusy] = useState(false);
  const [readbackNote, setReadbackNote] = useState<string | null>(null);

  const [reference, setReference] = useState("");
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmErr, setConfirmErr] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  // DLY-02 route actions.
  const [outcomes, setOutcomes] = useState<DeliveryOutcomeView | null>(null);
  const [driverId, setDriverId] = useState("");
  const [reassignBusy, setReassignBusy] = useState(false);
  const [reassignNote, setReassignNote] = useState<string | null>(null);
  const [parcelCondition, setParcelCondition] = useState<"intact" | "damaged" | "incomplete">("intact");
  const [evidenceRef, setEvidenceRef] = useState("");
  const [returnKey, setReturnKey] = useState(() => newRequestId());
  const [returnBusy, setReturnBusy] = useState(false);
  const [returnNote, setReturnNote] = useState<string | null>(null);
  const [compBusy, setCompBusy] = useState(false);
  const [compNote, setCompNote] = useState<string | null>(null);

  // NAT-03/G1: cancel/close a manual (non-Shopify) own-fleet stop.
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelNote, setCancelNote] = useState<string | null>(null);

  const load = useCallback(async (id: string) => {
    const trimmed = id.trim();
    if (!trimmed) return;
    setLoading(true);
    setErr(null);
    try {
      const [result, outcomeView] = await Promise.all([
        apiGet<DispatchAttemptsData>(`/dispatch/${trimmed}/attempts`),
        apiGet<DeliveryOutcomeView>(`/dispatch/${trimmed}/outcomes`).catch(() => null),
      ]);
      setData(result);
      setOutcomes(outcomeView);
    } catch (e) {
      setData(null);
      setOutcomes(null);
      setErr(e instanceof ApiError ? e.message : "Failed to load dispatch attempts");
    } finally {
      setLoading(false);
    }
  }, []);

  const runReadback = useCallback(async () => {
    setReadbackBusy(true);
    setReadbackNote(null);
    try {
      const body = taskRef.trim() ? { task_ref: taskRef.trim() } : {};
      const result = await apiPost<{ evidence: string; status?: string }>(
        `/dispatch/${deliveryId.trim()}/readback`,
        body,
      );
      setReadbackNote(
        result.evidence === "provider"
          ? `Provider evidence found: ${result.status ?? "unknown status"}.`
          : "No evidence at the provider for this reference.",
      );
      await load(deliveryId);
    } catch (e) {
      setReadbackNote(e instanceof ApiError ? e.message : "Read-back failed");
    } finally {
      setReadbackBusy(false);
    }
  }, [deliveryId, taskRef, load]);

  const runConfirm = useCallback(async () => {
    setConfirmBusy(true);
    setConfirmErr(null);
    try {
      await apiPost(`/dispatch/${deliveryId.trim()}/confirm-non-effect`, {
        courier_dashboard_reference: reference.trim(),
      });
      setConfirmed(true);
      await load(deliveryId);
    } catch (e) {
      setConfirmErr(e instanceof ApiError ? e.message : "Could not declare non-effect");
    } finally {
      setConfirmBusy(false);
    }
  }, [deliveryId, reference, load]);

  const runReassign = useCallback(async () => {
    setReassignBusy(true);
    setReassignNote(null);
    try {
      await apiPost(`/dispatch/${deliveryId.trim()}/assign`, { driver_id: driverId.trim() });
      setReassignNote("Reassigned.");
      setDriverId("");
      await load(deliveryId);
    } catch (e) {
      setReassignNote(e instanceof ApiError ? e.message : "Reassign failed");
    } finally {
      setReassignBusy(false);
    }
  }, [deliveryId, driverId, load]);

  const runReturnReceipt = useCallback(async () => {
    setReturnBusy(true);
    setReturnNote(null);
    try {
      await apiPost(`/dispatch/${deliveryId.trim()}/return-receipt`, {
        parcel_condition: parcelCondition,
        evidence_reference: evidenceRef.trim() || null,
        idempotency_key: returnKey,
      });
      setReturnNote("Return-to-origin receipt recorded.");
      setReturnKey(newRequestId());
      await load(deliveryId);
    } catch (e) {
      setReturnNote(e instanceof ApiError ? e.message : "Could not record the return receipt");
    } finally {
      setReturnBusy(false);
    }
  }, [deliveryId, parcelCondition, evidenceRef, returnKey, load]);

  const runCancel = useCallback(async () => {
    setCancelBusy(true);
    setCancelNote(null);
    try {
      await apiPost(`/dispatch/${deliveryId.trim()}/cancel`, { reason: cancelReason.trim() });
      setCancelNote("Stop cancelled.");
      setShowCancelForm(false);
      setCancelReason("");
      await load(deliveryId);
    } catch (e) {
      setCancelNote(e instanceof ApiError ? e.message : "Could not cancel the stop");
    } finally {
      setCancelBusy(false);
    }
  }, [deliveryId, cancelReason, load]);

  const runReturnCompensation = useCallback(async () => {
    setCompBusy(true);
    setCompNote(null);
    try {
      await apiPost(`/dispatch/${deliveryId.trim()}/return-compensation`, {});
      setCompNote("FIN-04 compensation obligation opened (or already open). No money moved.");
      await load(deliveryId);
    } catch (e) {
      setCompNote(e instanceof ApiError ? e.message : "Could not open the compensation obligation");
    } finally {
      setCompBusy(false);
    }
  }, [deliveryId, load]);

  const lookUp = useCallback(() => {
    // A fresh, explicit lookup clears any previous action's leftover state — unlike the
    // internal refresh `runReadback`/`runConfirm` do after their own call, which must NOT wipe
    // the success/notice banner it just set.
    setConfirmed(false);
    setReadbackNote(null);
    setConfirmErr(null);
    setReassignNote(null);
    setReturnNote(null);
    setCompNote(null);
    setCancelNote(null);
    setShowCancelForm(false);
    setCancelReason("");
    void load(deliveryId);
  }, [deliveryId, load]);

  const current = data?.attempts.find((a) => a.current);
  const canDeclare =
    !!current && current.state === "unknown" && !current.provider_task_ref && !!current.last_readback_attempted_at;
  const compensationState = (outcomes?.compensation as { state?: string } | undefined)?.state ?? null;
  const custodyHolder = (outcomes?.custody_holder as string | undefined) ?? null;

  return (
    <div className="board-page">
      <div className="board-header">
        <h1>Dispatch recovery</h1>
      </div>
      <p className="cap-note">
        Look up one delivery's courier booking attempts. An <code>unknown</code> attempt (the
        provider never confirmed or refused the booking) can only be declared a non-effect after a
        read-back has been attempted here and with a mandatory courier-dashboard reference — that
        is recorded as a permanent receipt and unblocks failover to the next provider.
      </p>

      <div className="recover-lookup">
        <input
          className="recover-input recover-wide"
          placeholder="Delivery id (UUID)"
          value={deliveryId}
          onChange={(e) => setDeliveryId(e.target.value)}
        />
        <button className="courier-btn" disabled={loading || !deliveryId.trim()} onClick={lookUp}>
          <Search size={12} /> {loading ? "Loading…" : "Look up"}
        </button>
      </div>

      {err && <div className="banner-err">{err}</div>}

      {data && (data.preparation_exceptions?.length ?? 0) > 0 && (
        <section className="banner-err" role="alert">
          <ShieldAlert size={16} /> Provider reported movement before this leg’s preparation ready time.
          Check the parcel and provider record; these statuses are reports, not physical proof.
          {data.preparation_exceptions?.map((item) => (
            <div key={item.id}>
              {statusLabel(item.provider_status)} · observed {new Date(item.observed_at).toLocaleString()}
              {item.prep_ready_at ? ` · ready ${new Date(item.prep_ready_at).toLocaleString()}` : " · ready time missing"}
            </div>
          ))}
        </section>
      )}

      {data && (
        <section className="lane dlq-lane">
          <header className="lane-head">
            <span>Attempts — dispatch_mode {statusLabel(data.dispatch_mode)}</span>
            <span className="lane-count">{data.attempts.length}</span>
          </header>
          <div className="lane-body">
            {data.attempts.length === 0 ? (
              <p className="lane-empty">No courier booking attempts for this delivery.</p>
            ) : (
              data.attempts.map((a) => (
                <div className="dlq-row" key={a.attempt_no}>
                  <span className={`dlq-status ${a.state === "unknown" ? "dlq-status-dead_letter" : ""}`}>
                    {statusLabel(a.state)}
                  </span>
                  <div className="dlq-main">
                    <span className="dlq-event">
                      Attempt #{a.attempt_no} · <span className="dlq-channel">{a.provider}</span>
                      {a.current ? " · current" : ""}
                    </span>
                    <span className="dlq-sub">
                      {a.provider_task_ref ? `task ${a.provider_task_ref}` : "no provider task ref"}
                      {a.outcome_reason ? ` · ${a.outcome_reason}` : ""}
                    </span>
                    {a.requires_attention && (
                      <span className="dlq-error">
                        <ShieldAlert size={11} /> {a.attention_reason || "requires attention"}
                      </span>
                    )}
                  </div>
                  <div className="dlq-meta">
                    <span className="dlq-time">
                      read-back: {a.last_readback_attempted_at ? new Date(a.last_readback_attempted_at).toLocaleString() : "never"}
                    </span>
                    {a.resolved_at && <span className="dlq-time-sub">resolved {new Date(a.resolved_at).toLocaleString()}</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </section>
      )}

      {data && current && current.state === "unknown" && (
        <section className="lane">
          <header className="lane-head">
            <span>Resolve the current unknown attempt</span>
          </header>
          <div className="lane-body">
            {!canAct && (
              <p className="cap-note">Your role ({user?.role}) can view this but not act — that needs owner, admin or dispatcher.</p>
            )}

            <div className="recover-step">
              <p className="cap-note">
                1. Attempt a provider read-back (optionally with a task reference you found at the
                provider's own dashboard — otherwise this delivery's own tracking token is tried).
              </p>
              <div className="recover-lookup">
                <input
                  className="recover-input"
                  placeholder="Optional: provider task reference"
                  value={taskRef}
                  onChange={(e) => setTaskRef(e.target.value)}
                  disabled={!canAct}
                />
                <button className="courier-btn" disabled={!canAct || readbackBusy} onClick={() => void runReadback()}>
                  {readbackBusy ? "Checking…" : "Attempt read-back"}
                </button>
              </div>
              {readbackNote && <p className="cap-note">{readbackNote}</p>}
            </div>

            <div className="recover-step">
              <p className="cap-note">
                2. Once a read-back has been attempted, declare this attempt a non-effect (the
                provider created nothing) — mandatory: what you checked at the courier's dashboard.
              </p>
              <textarea
                className="recover-textarea"
                placeholder="e.g. DoorDash dashboard shows no delivery for order #1009 as of 14:02 UTC."
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                disabled={!canAct || !canDeclare}
              />
              <div className="recover-actions">
                <button
                  className="courier-btn dlq-requeue"
                  disabled={!canAct || !canDeclare || confirmBusy || !reference.trim()}
                  onClick={() => void runConfirm()}
                >
                  <RotateCcw size={12} /> {confirmBusy ? "Declaring…" : "Declare non-effect"}
                </button>
                {!canDeclare && <span className="cap-note">Attempt a read-back first.</span>}
              </div>
              {confirmErr && <p className="dlq-error">{confirmErr}</p>}
              {confirmed && <p className="cap-note">Declared. Failover is now unblocked for this delivery.</p>}
            </div>
          </div>
        </section>
      )}

      {data && (
        <section className="lane">
          <header className="lane-head">
            <span><Truck size={13} /> Own-fleet reassign</span>
          </header>
          <div className="lane-body">
            <p className="cap-note">
              Custody-aware: a leg held by another rider or a courier only moves here after that
              carrier's parcel is receipted back to origin (below) — the server refuses otherwise.
              A live courier leg is cancelled first, never left orphaned.
            </p>
            <div className="recover-lookup">
              <input
                className="recover-input"
                placeholder="Driver id (UUID)"
                value={driverId}
                onChange={(e) => setDriverId(e.target.value)}
                disabled={!canAct}
              />
              <button className="courier-btn" disabled={!canAct || reassignBusy || !driverId.trim()} onClick={() => void runReassign()}>
                {reassignBusy ? "Assigning…" : "Reassign"}
              </button>
            </div>
            {reassignNote && <p className="cap-note">{reassignNote}</p>}
          </div>
        </section>
      )}

      {data && ["unassigned", "own_fleet"].includes(data.dispatch_mode) && (
        <section className="lane">
          <header className="lane-head">
            <span><Ban size={13} /> Cancel manual stop</span>
          </header>
          <div className="lane-body">
            <p className="cap-note">
              Closes this stop as <code>cancelled</code> -- it does not claim the parcel physically
              came back. A COD stop's collected cash stays exactly where the custody ledger says it
              is; cancelling never creates a refund, and any cash already collected must still be
              handed over through the normal cash endpoints. Refused for a Shopify-linked stop, a
              courier/dispatch-pending leg (use its own cancel path), or a stop already terminal.
            </p>
            {!showCancelForm ? (
              <button className="courier-btn" disabled={!canAct} onClick={() => { setShowCancelForm(true); setCancelNote(null); }}>
                Cancel stop…
              </button>
            ) : (
              <>
                <textarea
                  className="recover-textarea"
                  placeholder="Required reason, e.g. Vendor withdrew the order."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  disabled={!canAct}
                />
                <div className="recover-actions">
                  <button
                    className="courier-btn"
                    disabled={!canAct || cancelBusy || cancelReason.trim().length < 3}
                    onClick={() => void runCancel()}
                  >
                    {cancelBusy ? "Cancelling…" : "Confirm cancel"}
                  </button>
                  <button className="courier-btn" disabled={cancelBusy} onClick={() => { setShowCancelForm(false); setCancelNote(null); }}>
                    Back
                  </button>
                </div>
              </>
            )}
            {cancelNote && <p className="cap-note">{cancelNote}</p>}
          </div>
        </section>
      )}

      {data && (
        <section className="lane">
          <header className="lane-head">
            <span><Undo2 size={13} /> Returned to origin — custody receipt &amp; compensation</span>
          </header>
          <div className="lane-body">
            <p className="cap-note">
              Custody holder: {custodyHolder ?? "unknown"} · Compensation: {compensationState ?? "not read"}
            </p>
            <div className="recover-step">
              <p className="cap-note">1. Acknowledge the parcel is physically back at the origin (idempotent).</p>
              <div className="recover-lookup">
                <select
                  className="recover-input"
                  value={parcelCondition}
                  onChange={(e) => setParcelCondition(e.target.value as typeof parcelCondition)}
                  disabled={!canAct}
                >
                  <option value="intact">intact</option>
                  <option value="damaged">damaged</option>
                  <option value="incomplete">incomplete</option>
                </select>
                <input
                  className="recover-input recover-wide"
                  placeholder="Optional evidence reference"
                  value={evidenceRef}
                  onChange={(e) => setEvidenceRef(e.target.value)}
                  disabled={!canAct}
                />
                <button className="courier-btn" disabled={!canAct || returnBusy} onClick={() => void runReturnReceipt()}>
                  {returnBusy ? "Recording…" : "Record return receipt"}
                </button>
              </div>
              {returnNote && <p className="cap-note">{returnNote}</p>}
            </div>
            <div className="recover-step">
              <p className="cap-note">
                2. Open the FIN-04 compensation obligation for a prepaid leg returned to origin (owner/admin — a
                money decision; no money moves, only the obligation record).
              </p>
              <div className="recover-actions">
                <button className="courier-btn" disabled={!canMoney || compBusy} onClick={() => void runReturnCompensation()}>
                  {compBusy ? "Opening…" : "Open compensation"}
                </button>
                {!canMoney && <span className="cap-note">Your role ({user?.role}) cannot open a compensation obligation — needs owner or admin.</span>}
              </div>
              {compNote && <p className="cap-note">{compNote}</p>}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
